import * as React from 'react';
import { Button } from '@material-ui/core';
import { httpClient } from '../utils/dataProvider';
import { Empty, PageHeader, Row, RowMeta, RowText, RowTitle, Search, StatusPill, Surface, Toolbar, MetricCard } from '../components/ui';

const TIER_COLORS = {
    CLEAN: '#22c55e',
    WARNING: '#eab308',
    FREEZE_24H: '#f97316',
    FREEZE_3D: '#ef4444',
    SUSPENDED: '#dc2626',
};

const TIER_TONES = {
    CLEAN: 'active',
    WARNING: 'warning',
    FREEZE_24H: 'warning',
    FREEZE_3D: 'banned',
    SUSPENDED: 'banned',
};

export default function CancellationConsole() {
    const [activeTab, setActiveTab] = React.useState('events');
    const [events, setEvents] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [ladderSearch, setLadderSearch] = React.useState('');
    const [ladderUser, setLadderUser] = React.useState(null);
    const [ladderLoading, setLadderLoading] = React.useState(false);
    const [overrideLoading, setOverrideLoading] = React.useState(false);
    const [flaggedUsers, setFlaggedUsers] = React.useState([]);
    const [flaggedLoading, setFlaggedLoading] = React.useState(false);

    const loadEvents = () => {
        setLoading(true);
        httpClient('https://skilzapp.net/api/cancellationEvents').then(({ json }) => {
            setEvents(json.docs || json || []);
        }).catch(console.error).finally(() => setLoading(false));
    };

    const loadFlagged = () => {
        setFlaggedLoading(true);
        httpClient('https://skilzapp.net/api/users/flagged-cancellations').then(({ json }) => {
            setFlaggedUsers(json.docs || json || []);
        }).catch(console.error).finally(() => setFlaggedLoading(false));
    };

    React.useEffect(() => { if (activeTab === 'events') loadEvents(); }, [activeTab]);
    React.useEffect(() => { if (activeTab === 'ladders') loadFlagged(); }, [activeTab]);

    const filteredEvents = React.useMemo(() => {
        if (!search) return events;
        const q = search.toLowerCase();
        return events.filter(e =>
            (e.actor?.name || '').toLowerCase().includes(q) ||
            (e.reason || '').toLowerCase().includes(q) ||
            (e.request || '').toString().toLowerCase().includes(q)
        );
    }, [events, search]);

    const typeCounts = React.useMemo(() => {
        const c = {};
        events.forEach(e => {
            const label = e.eventType || 'UNKNOWN';
            c[label] = (c[label] || 0) + 1;
        });
        return Object.entries(c).sort((a, b) => b[1] - a[1]);
    }, [events]);

    const selectFlaggedUser = (user) => {
        setLadderUser({
            name: user.name,
            phone: user.phone,
            id: user._id,
            status: user.status,
            cancellationPoints: user.cancellationPoints || 0,
            cancellationTier: user.cancellationTier || 'CLEAN',
            frozenUntil: user.frozenUntil,
            frozenReason: user.frozenReason,
        });
    };

    const searchLadder = () => {
        if (!ladderSearch.trim()) return;
        setLadderLoading(true);
        setLadderUser(null);
        httpClient(`https://skilzapp.net/api/users?filter=${encodeURIComponent(ladderSearch)}&fields=name,phone,status,cancellationPoints,cancellationTier,frozenUntil,frozenReason`).then(({ json }) => {
            const users = json.docs || json || [];
            if (users.length === 0) {
                setLadderUser({ error: 'No user found' });
                return;
            }
            const user = users[0];
            selectFlaggedUser(user);
        }).catch(console.error).finally(() => setLadderLoading(false));
    };

    const handleOverride = (action, userId, userName) => {
        const targetId = userId || ladderUser?.id;
        const targetName = userName || ladderUser?.name;
        if (!targetId) return;
        const confirmMsgs = {
            unfreeze: `Unfreeze user ${targetName}? This will restore their account to active.`,
            unsuspend: `Unsuspend user ${targetName}? This will restore their account to active.`,
            reset: `RESET user ${targetName}? This will clear ALL cancellation history, points, and enforcement state. This cannot be undone.`,
        };
        if (!window.confirm(confirmMsgs[action] || 'Continue?')) return;

        setOverrideLoading(true);
        httpClient(`https://skilzapp.net/api/users/${targetId}/cancellation-override`, {
            method: 'POST',
            body: JSON.stringify({ action }),
        }).then((res) => {
            if (action === 'reset' && res.json?.deletedEvents != null) {
                alert(`Reset complete. ${res.json.deletedEvents} cancellation event(s) deleted.`);
            }
            // Refresh both the selected user and the flagged list
            if (ladderUser?.id === targetId) searchLadder();
            loadFlagged();
        }).catch((err) => {
            console.error('Override failed:', err);
            alert('Override failed. Check console for details.');
        }).finally(() => setOverrideLoading(false));
    };

    const eventTypeLabel = (type) => {
        const labels = {
            'CANCELLATION': 'Cancelled',
            'NO_SHOW_CLAIM': 'No-Show',
            'AUTO_CANCEL': 'Auto-Cancelled',
            'COMPLETION': 'Completed',
            'AGREED_CANCEL': 'Agreed Cancel',
        };
        return labels[type] || type || 'UNKNOWN';
    };

    const eventTypeTone = (type) => {
        if (type === 'NO_SHOW_CLAIM') return 'banned';
        if (type === 'AUTO_CANCEL') return 'banned';
        if (type === 'AGREED_CANCEL') return 'warning';
        if (type === 'COMPLETION') return 'active';
        return 'suspended';
    };

    const tierLabel = (tier) => {
        const labels = {
            CLEAN: 'Clean',
            WARNING: 'Warning',
            FREEZE_24H: '24h Freeze',
            FREEZE_3D: '3-Day Freeze',
            SUSPENDED: 'Suspended',
        };
        return labels[tier] || tier || 'Unknown';
    };

    const statusTone = (status) => {
        if (status === 'frozen') return 'warning';
        if (status === 'suspended') return 'banned';
        return 'active';
    };

    const linkStyle = { color: '#7c3aed', textDecoration: 'none', cursor: 'pointer', fontWeight: 600 };

    return <main className="sk-page">
        <PageHeader eyebrow="Operations" title="Cancellation Console" description="Monitor cancellation events, inspect user ladder status." />
        <div className="sk-tab-row" role="tablist" aria-label="Cancellation view">
            <Button role="tab" aria-selected={activeTab === 'events'} variant={activeTab === 'events' ? 'contained' : 'outlined'} color="primary" onClick={() => setActiveTab('events')}>Events</Button>
            <Button role="tab" aria-selected={activeTab === 'ladders'} variant={activeTab === 'ladders' ? 'contained' : 'outlined'} color="primary" onClick={() => setActiveTab('ladders')}>User Ladders</Button>
        </div>

        {activeTab === 'events' && <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <MetricCard label="Total Events" value={events.length} detail="All cancellation events" />
                {typeCounts.slice(0, 3).map(([type, count]) =>
                    <MetricCard key={type} tone="orange" label={eventTypeLabel(type)} value={count} detail={`${((count / events.length) * 100).toFixed(0)}% of events`} />
                )}
            </div>
            <Surface title="Cancellation Events" description={`${filteredEvents.length} events`}>
                <Toolbar right={<Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, reason, request…" />} />
                {loading && <div className="sk-state is-loading" role="status">Loading…</div>}
                {!loading && !filteredEvents.length && <Empty>No cancellation events found.</Empty>}
                {filteredEvents.slice(0, 100).map(e => <Row key={e._id} right={<StatusPill status={eventTypeTone(e.eventType)}>{eventTypeLabel(e.eventType)}</StatusPill>}>
                    <RowText>
                        <RowTitle>
                            {e.actor?._id
                                ? <a href={`#/users/${e.actor._id}/show`} style={linkStyle}>{e.actor.name}</a>
                                : 'System'}
                        </RowTitle>
                        <RowMeta>
                            Request: {e.request
                                ? <a href={`#/requests/${e.request}/show`} style={linkStyle}>{e.request}</a>
                                : '—'}
                            {' · '}Role: {e.actorRole || '—'}
                            {' · '}Reason: {e.reason || '—'}
                        </RowMeta>
                        <RowMeta dim>{e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}</RowMeta>
                    </RowText>
                </Row>)}
            </Surface>
        </>}

        {activeTab === 'ladders' && <>
            <Surface title="Lookup User Ladder" description="Search by name or phone to see their cancellation enforcement status.">
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Search user</label>
                        <input
                            value={ladderSearch}
                            onChange={e => setLadderSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && searchLadder()}
                            placeholder="Name or phone…"
                            style={{
                                width: '100%',
                                padding: '8px 14px',
                                borderRadius: 12,
                                border: '2px solid #a855f7',
                                outline: 'none',
                                fontSize: 14,
                                transition: 'border-color 0.2s',
                            }}
                            onFocus={e => { e.target.style.borderColor = '#7c3aed'; }}
                            onBlur={e => { e.target.style.borderColor = '#a855f7'; }}
                        />
                    </div>
                    <Button variant="contained" color="primary" disabled={ladderLoading} onClick={searchLadder}>{ladderLoading ? 'Searching…' : 'Look Up'}</Button>
                </div>
            </Surface>

            {/* Selected user detail */}
            {ladderUser && !ladderUser.error && <Surface title={ladderUser.name} description={ladderUser.phone}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                    <MetricCard tone="blue" label="Points" value={ladderUser.cancellationPoints} detail="Lifetime cancellation points" />
                    <MetricCard
                        label="Cancellation Tier"
                        value={tierLabel(ladderUser.cancellationTier)}
                        detail="From API enforcement ladder"
                        tone={TIER_TONES[ladderUser.cancellationTier] || 'green'}
                    />
                    <MetricCard label="Status" value={ladderUser.status || 'active'} detail="Account status" />
                    {ladderUser.frozenUntil && (
                        <MetricCard tone="orange" label="Frozen Until" value={new Date(ladderUser.frozenUntil).toLocaleString()} detail={ladderUser.frozenReason || 'Ladder escalation'} />
                    )}
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                    <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: TIER_COLORS[ladderUser.cancellationTier] || '#6b7280',
                    }}>
                        Tier: {tierLabel(ladderUser.cancellationTier)}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                    {ladderUser.status === 'frozen' && (
                        <Button
                            variant="contained"
                            style={{ backgroundColor: '#22c55e', color: '#fff' }}
                            disabled={overrideLoading}
                            onClick={() => handleOverride('unfreeze')}
                        >
                            {overrideLoading ? 'Processing…' : 'Unfreeze User'}
                        </Button>
                    )}
                    {ladderUser.status === 'suspended' && (
                        <Button
                            variant="contained"
                            style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                            disabled={overrideLoading}
                            onClick={() => handleOverride('unsuspend')}
                        >
                            {overrideLoading ? 'Processing…' : 'Unsuspend User'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        style={{ backgroundColor: '#ef4444', color: '#fff', borderRadius: 14 }}
                        disabled={overrideLoading}
                        onClick={() => handleOverride('reset')}
                    >
                        {overrideLoading ? 'Processing…' : '🔄 Reset User'}
                    </Button>
                </div>
            </Surface>}
            {ladderUser?.error && <Surface><span className="sk-muted">{ladderUser.error}</span></Surface>}

            {/* Flagged users list */}
            <Surface title="Flagged Users" description="Users with cancellation points, non-CLEAN tier, or enforcement applied.">
                {flaggedLoading && <div className="sk-state is-loading" role="status">Loading…</div>}
                {!flaggedLoading && !flaggedUsers.length && <Empty>No flagged users. All clear.</Empty>}
                {flaggedUsers.map(u => <Row key={u._id}
                    right={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <StatusPill status={statusTone(u.status)}>{u.status || 'active'}</StatusPill>
                        <span style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: TIER_COLORS[u.cancellationTier] || '#6b7280',
                        }}>
                            {tierLabel(u.cancellationTier)}
                        </span>
                    </div>}
                >
                    <RowText>
                        <RowTitle>
                            <a href={`#/users/${u._id}/show`} style={linkStyle}>{u.name || 'Unknown'}</a>
                        </RowTitle>
                        <RowMeta>{u.phone || '—'} · {u.cancellationPoints || 0} points</RowMeta>
                    </RowText>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        <Button size="small" style={{ fontSize: 11, color: '#7c3aed' }} onClick={() => selectFlaggedUser(u)}>
                            Inspect
                        </Button>
                        <Button size="small" style={{ fontSize: 11, color: '#ef4444' }} onClick={() => handleOverride('reset', u._id, u.name)}>
                            Reset
                        </Button>
                    </div>
                </Row>)}
            </Surface>
        </>}
    </main>;
}
